// NEXT BUTTON HANDLER (Used in timeline & form btns)
/**
 * @description This function is used to handle the next button in the form. The function also handles creating the form. It is used in the timeline and form buttons.
 * @param {Event} e - The event object.
 * @param {string} clientId - The client ID.
 * @param {string} currentProjectID - The current project ID.
 * @param {Object} slide1 - The slide1 object.
 * @param {number} pageNumber - The current page number.
 * @param {function} setPageNumber - The function to set the page number.
 * @param {number} newPageNumber - The new page number.
 * @param {Object} createForm - The createForm object.
 * @param {Object} queryClient - The queryClient object.
 */
export async function handleNext(
  e,
  clientId,
  currentProjectID,
  slide1,
  pageNumber,
  setPageNumber,
  newPageNumber,
  createForm,
  queryClient,
) {
  e.preventDefault();
  // if the page number is 1 and there is no current project ID, create the form
  if (pageNumber === 1 && !currentProjectID) {
    await createForm.mutateAsync(slide1);
  } else if (pageNumber > 0 && currentProjectID) {
    // if the page number is greater than 0 and there is a current project ID, invalidate the predictiveText query
    queryClient.invalidateQueries({
      queryKey: ["predictiveText", clientId],
    });
    // Update the page number
    setPageNumber(+newPageNumber);
  } else if (pageNumber === 0) {
    // if the page number is 0, update the page number
    setPageNumber(+newPageNumber);
  }
}

// Verify that the email matched proper formatting
export const isValidEmail = (email, optional = true) => {
  if (optional === true && email === "") return true;
  const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
  return emailPattern.test(email);
};

// Verify that the name matched proper formatting
export const isValidName = (name, optional = true) => {
  // must include at least first and last name separated by a space & can include a hyphen (-)
  if (optional === true && name === "") return true;

  let nameArray = name.split(" ");

  return nameArray.length >= 2 && nameArray[1] !== "";
};

// Verify that the phone number matched proper formatting
export const isValidPhoneNumber = (phone, optional = true) => {
  // must be exactly 12 digits long (dashes are dynamically added) (###-###-####)
  if (optional === true && phone.length === 0) return true;
  if (optional === false && phone.length === 0) return false;
  const phonePattern = /^\d{3}-\d{3}-\d{4}$/;
  return phonePattern.test(phone);
};

// Verify that the form is ready to be submitted
export const verifySubmission = (slideStatus) => {
  // if all slides except slide9 have a value equal === 'complete' then return true
  // slideStatus is an object with keys 'slide1', 'slide2', up to 'slide10' and the values can be 'complete', or 'incomplete'
  let verified = false;
  for (let slide in slideStatus) {
    if (
      slide === "slide10" &&
      slideStatus["slide1"] === "complete" &&
      slideStatus["slide2"] === "complete" &&
      slideStatus["slide3"] === "complete" &&
      slideStatus["slide4"] === "complete" &&
      slideStatus["slide5"] === "complete" &&
      slideStatus["slide6"] === "complete" &&
      slideStatus["slide7"] === "complete" &&
      slideStatus["slide8"] === "complete"
    ) {
      verified = true;
      continue;
    } else if (
      slide === "slide10" &&
      (slideStatus["slide1"] === "incomplete" ||
        slideStatus["slide2"] === "incomplete" ||
        slideStatus["slide3"] === "incomplete" ||
        slideStatus["slide4"] === "incomplete" ||
        slideStatus["slide5"] === "incomplete" ||
        slideStatus["slide6"] === "incomplete" ||
        slideStatus["slide7"] === "incomplete" ||
        slideStatus["slide8"] === "incomplete")
    ) {
      verified = false;
      break;
    } else {
      verified = true;
    }
  }
  return verified;
};

export const getMissingValues = (slides) => {
  const [
    slide1,
    slide2,
    slide3,
    slide4,
    slide5,
    slide6,
    slide7,
    slide8,
    slide9,
  ] = slides;
  const missingValues = {
    slide1: [],
    slide2: [],
    slide3: [],
    slide4: [],
    slide5: [],
    slide6: [],
    slide7: [],
    slide8: [],
    slide9: [],
    slide10: [],
  };
  // Slide 1
  if (slide1.pointOfContact === "" || slide1.pointOfContactEmail === "") {
    missingValues.slide1.push("Point of Contact (Name & Email Needed)");
  }
  if (!slide1.accountingContact || !slide1.accountingEmail) {
    missingValues.slide1.push("Billing Contact (Name & Email Needed)");
  }
  if (!slide1.jobNumber) {
    missingValues.slide1.push("Job Number");
  }

  // Slide 2
  if (slide2.submittingToRegAgency === "Yes" && !slide2.regAgencySubmitDate) {
    missingValues.slide2.push("SWPPP Needed By");
  }
  if (
    slide2.activeCDX === "Yes" &&
    (!slide2.cdxAccountManager || !slide2.cdxUserId)
  ) {
    missingValues.slide2.push("CDX Account Manager & User ID Needed");
  }
  if (
    slide2.inspectionRequest === "Yes" &&
    (!slide2.inspectionRecipient1 ||
      !slide2.inspectionRecipient1Title ||
      !slide2.inspectionRecipient1Email)
  ) {
    missingValues.slide2.push(
      "Inspection Recipient 1 (Name, Title & Email Needed)",
    );
  }
  if (slide2.inspectionRequest === "No" && !slide2.inspectingCompany) {
    missingValues.slide2.push("Inspection Company");
  }
  if (
    slide2.inspectionRequest === "No" &&
    (!slide2.inspectorName || !slide2.inspectorEmail || !slide2.inspectorPhone)
  ) {
    missingValues.slide2.push(
      "Inspector Information (Name, Email & Phone Needed)",
    );
  }

  // Slide 3
  if (
    !slide3.t1 &&
    !slide3.t2 &&
    !slide3.t3 &&
    !slide3.t4 &&
    !slide3.t5 &&
    !slide3.t6 &&
    !slide3.t7 &&
    !slide3.t8 &&
    !slide3.t9 &&
    !slide3.t10 &&
    !slide3.t11 &&
    !slide3.t12 &&
    !slide3.t13 &&
    !slide3.other
  ) {
    missingValues.slide3.push("Project Type");
  }
  if (!slide3.scopeOfWork) {
    missingValues.slide3.push("Scope of Work Description");
  }

  // Slide 4
  if (!slide4.startDate || !slide4.endDate) {
    missingValues.slide4.push("Project Duration");
  }
  if (
    !slide4.emergencyContactName ||
    !slide4.emergencyContactPhone ||
    !slide4.emergencyContactEmail ||
    !slide4.emergencyContactTitle
  ) {
    missingValues.slide4.push(
      "Emergency Contact (Name, Phone, Email & Title Needed)",
    );
  }
  if (!slide4.totAcr || !slide4.distAcr) {
    missingValues.slide4.push("Total & Disturbed Acreage");
  }
  if (!slide4.bmpCompanyName) {
    missingValues.slide4.push("BMP Company");
  }

  if (
    !slide4.bmpContactName ||
    !slide4.bmpContactEmail ||
    !slide4.bmpContactPhone
  ) {
    missingValues.slide4.push("BMP Contact (Name, Email & Phone Needed)");
  }

  // Slide 5
  if (!slide5.pAdd || !slide5.pCity || !slide5.pCounty || !slide5.pZip) {
    missingValues.slide5.push(
      "Project Address (Address, City, State & Zip Needed)",
    );
  }

  // Slide 6
  if (
    !slide6.oName ||
    !slide6.oPhone ||
    !slide6.oAdd ||
    !slide6.oCity ||
    !slide6.oState ||
    !slide6.oZip
  ) {
    missingValues.slide6.push(
      "Owning Entity Information (Name, Phone, Address, City, State & Zip Needed)",
    );
  }
  if (
    !slide6.oContactName ||
    !slide6.oContactPhone ||
    !slide6.oContactEmail ||
    !slide6.oContactTitle
  ) {
    missingValues.slide6.push(
      "Owner's Responsible Officer (Name, Phone, Email & Title Needed)",
    );
  }
  if (
    slide6.delegatedRepCheck === "Yes" &&
    (!slide6.odrCompany ||
      !slide6.odrName ||
      !slide6.odrPhone ||
      !slide6.odrEmail ||
      !slide6.odrTitle)
  ) {
    missingValues.slide6.push(
      "Owner's Delegated Individual (Company, Name, Phone, Email & Title Needed)",
    );
  }

  // Slide 7
  if (
    !slide7.gcName ||
    !slide7.gcPhone ||
    !slide7.gcAdd ||
    !slide7.gcCity ||
    !slide7.gcState ||
    !slide7.gcZip
  ) {
    missingValues.slide7.push(
      "Operator/General Contractor Information (Name, Phone, Address, City, State & Zip Needed)",
    );
  }
  if (
    !slide7.gcContactName ||
    !slide7.gcContactPhone ||
    !slide7.gcContactEmail ||
    !slide7.gcContactTitle
  ) {
    missingValues.slide7.push(
      "Operator/General Contractor Responsible Officer (Name, Phone, Email & Title Needed)",
    );
  }
  if (
    !slide7.gcContact2Name ||
    !slide7.gcContact2Phone ||
    !slide7.gcContact2Email ||
    !slide7.gcContact2Title
  ) {
    missingValues.slide7.push(
      "Day-to-day Operations Contact (Name, Phone, Email & Title Needed)",
    );
  }
  if (
    !slide7.gcContact3Name ||
    !slide7.gcContact3Phone ||
    !slide7.gcContact3Email ||
    !slide7.gcContact3Title
  ) {
    missingValues.slide7.push(
      "Field Operations Contact (Name, Phone, Email & Title Needed)",
    );
  }

  // Slide 8
  if (slide8.dustControlRequest === "Yes") {
    if (
      slide8.manualLogsRequest === "Yes" &&
      (!slide8.dcc || !slide8.dccEmail || !slide8.dccPhone || !slide8.dccTitle)
    ) {
      missingValues.slide8.push(
        "Dust Control Coordinator (Name, Email, Phone & Title Needed)",
      );
    }
  }

  if (slide8.dustAuditRequest === "Yes") {
    if (
      !slide8.dustAuditContact ||
      !slide8.dustAuditContactEmail ||
      !slide8.dustAuditContactPhone
    ) {
      missingValues.slide8.push(
        "Dust Audit Contact (Name, Email & Phone Needed)",
      );
    }
  }

  if (slide8.dustSignRequest === "Yes") {
    if (!slide8.dcName || !slide8.dcPhone) {
      missingValues.slide8.push(
        "Dust Sign Contact (Name, Email & Phone Needed)",
      );
    }
  }

  // Slide 9
  if (slide9.safetyInterest === "Yes") {
    if (slide9.level1Safety !== "Yes" && slide9.level2Safety !== "Yes") {
      missingValues.slide9.push("Safety Audit (Level 1 or Level 2)");
    }
  }

  console.log("MISSING VALUES: ", missingValues);
  return missingValues;
};
